<template>
  <main>
    <div class="container">
      <div class="relative mb-24 mt-8">
        <BackButton
          v-if="uiState !== UI_STATE_SUCCESS && !isLoading"
          :custom-click="uiState !== UI_STATE_PRODUCT"
          @click="setStateBack()"
        />

        <LoadingElement
          v-if="isLoading && !isEvaluatedEditMode"
          :overlay="true"
          class="h-full w-full bg-white/80"
        />

        <template v-if="uiState === UI_STATE_PRODUCT || isEvaluatedEditMode">
          <EditableArea :content="productSelectionStep" />
          <ProductRegistrationSelectionStep />
        </template>

        <template v-if="uiState === UI_STATE_LOGIN || isEvaluatedEditMode">
          <EditableArea :content="loginStep" />
          <ProductRegistrationLoginStep @guest-proceed="guestRegistration = true" />
        </template>

        <template v-if="uiState === UI_STATE_PERSONAL || isEvaluatedEditMode">
          <EditableArea :content="personalInformationStep" />
          <ProductRegistrationPersonalInformationForm
            @submit-personal-data="editPersonalData = false"
          >
            <ProductRegistrationNewsletterComponent
              v-if="!isEvaluatedEditMode"
              :content="productRegistrationNewsletter"
              :ui-state="uiState"
              :email="email"
            />
          </ProductRegistrationPersonalInformationForm>
        </template>

        <template v-if="uiState === UI_STATE_REVIEW || isEvaluatedEditMode">
          <EditableArea :content="reviewStep" />
          <ProductRegistrationReviewStep
            :selected-product="selectedProduct"
            :captcha-solution="captchaSolution"
            @change-personal-data="editPersonalData = true"
            @submit-registration="submitRegistration()"
          >
            <ProductRegistrationNewsletterComponent
              v-if="!isEvaluatedEditMode"
              ref="newsletterComponent"
              :content="productRegistrationNewsletter"
              :ui-state="uiState"
              :email="email"
            />
            <VueFriendlyCaptcha
              ref="captcha"
              :sitekey="siteKey"
              :language="captchaLanguage"
              @done="
                (solution) => {
                  captchaSolution = solution;
                }
              "
            />
          </ProductRegistrationReviewStep>
        </template>

        <template v-if="uiState === UI_STATE_SUCCESS || isEvaluatedEditMode">
          <ProductRegistrationSuccessStep>
            <EditableArea :content="successStep" />
            <ProductRegistrationNewsletterComponent
              v-if="!isEvaluatedEditMode"
              :content="productRegistrationNewsletter"
              :ui-state="uiState"
              :email="email"
            />
          </ProductRegistrationSuccessStep>
        </template>

        <div v-if="isEvaluatedEditMode" class="order-first">
          <EditableArea :content="productRegistrationNewsletter">
            <ProductRegistrationNewsletterComponent :content="productRegistrationNewsletter" />
          </EditableArea>
        </div>
      </div>
    </div>
    <EditableArea v-if="sectionArea" :content="sectionArea" />
  </main>
</template>

<script setup>
import { EditableArea } from "@magnolia/vue-editor";
import useProductRegistrationState from "~/composables/shop/useProductRegistrationState";
import { useProductRegistration } from "~/composables/shop/useProductRegistration";
import { isEditMode } from "~/utils/content/magnolia";
import useProductRegistrationPersonalInformation from "~/composables/shop/useProductRegistrationPersonalInformation";
import useBritaMiddleware from "~/composables/useBritaMiddleware";
import BackButton from "~/templates/components/BackButton.vue";
import ProductRegistrationNewsletterComponent from "~/templates/components/cms/ProductRegistrationNewsletterComponent.vue";
import ProductRegistrationSelectionStep from "~/templates/components/shop/productRegistration/ProductRegistrationSelectionStep.vue";
import LoadingElement from "~/templates/elements/LoadingElement.vue";
import { useProductRegistrationNewsletter } from "~/composables/shop/useProductRegistrationNewsletter.ts";
import VueFriendlyCaptcha from "@somushq/vue3-friendly-captcha";
import useCaptcha from "~/composables/useCaptcha";

const isEvaluatedEditMode = isEditMode();

const props = defineProps([
  "productSelectionStep",
  "reviewStep",
  "personalInformationStep",
  "loginStep",
  "successStep",
  "productRegistrationNewsletter",
  "newsletterArea",
  "sectionArea",
]);

// Captcha

const captcha = ref();
const { captchaSolution, captchaLanguage, siteKey } = useCaptcha();

const { consentCheck, submitNewsletterRegistration } = useProductRegistrationNewsletter();

const { customerType } = await useProductRegistrationPersonalInformation();
const { allProducts } = await useProductRegistration();
const { createRegistration } = useBritaMiddleware();
const { hasProduct, registrationState, addMultipleValues, removeProduct, deleteRegistrationState } =
  await useProductRegistrationState();
const { pushError } = useNotifications();
const { verifyLogIn, isMyBritaLoggedIn } = useMyBritaUser();
const { azureUser } = useAzureUser();

const selectedProduct = computed(() => {
  return allProducts.value?.find((product) => product.id === registrationState.value?.["id"]);
});

const newsletterComponent = ref(null);
const productRegistrationSuccess = ref(false);
const guestRegistration = ref(false);
const editPersonalData = ref(true);
const isLoading = ref(true);
const email = ref("");

watch(registrationState, () => {
  email.value = registrationState.value?.bri_email;
});

const UI_STATE_PRODUCT = "selection-step";
const UI_STATE_LOGIN = "login-step";
const UI_STATE_PERSONAL = "personal-information-step";
const UI_STATE_REVIEW = "review-step";
const UI_STATE_SUCCESS = "success-step";

const uiState = computed(() => {
  if (productRegistrationSuccess.value) {
    return UI_STATE_SUCCESS;
  }
  if (!hasProduct.value) {
    return UI_STATE_PRODUCT;
  } else if (customerType.value === 0 && !guestRegistration.value) {
    return UI_STATE_LOGIN;
  } else if (editPersonalData.value) {
    return UI_STATE_PERSONAL;
  } else if ((customerType.value === 1 || guestRegistration.value) && hasProduct.value) {
    return UI_STATE_REVIEW;
  }
  return UI_STATE_PRODUCT;
});

const setStateBack = () => {
  if (
    uiState.value === UI_STATE_LOGIN ||
    ((uiState.value === UI_STATE_PERSONAL || UI_STATE_REVIEW) && isMyBritaLoggedIn.value)
  ) {
    removeProduct();
  } else if (uiState.value === UI_STATE_PERSONAL && !isMyBritaLoggedIn.value) {
    guestRegistration.value = false;
  } else {
    editPersonalData.value = true;
  }
};
const submitRegistration = async () => {
  isLoading.value = true;
  addMultipleValues({
    bri_customertype: customerType?.value,
    bri_token: azureUser.value?.idToken ?? "",
  });

  try {
    await createRegistration({
      ...registrationState.value,
      bwfCaptcha: captchaSolution.value,
    });
    productRegistrationSuccess.value = true;

    if (consentCheck.value) {
      await submitNewsletterRegistration(
        email.value,
        props.productRegistrationNewsletter?.actionId,
        props.productRegistrationNewsletter?.interactionId,
      );
    }
    // remove existing local storage item on successful registration
    deleteRegistrationState();
  } catch (e) {
    console.error(e);
    pushError(e);
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  try {
    await verifyLogIn();
  } catch (e) {
    console.log("ProductRegistrationPage: could not verify login, will treat user as guest");
  } finally {
    isLoading.value = false;
  }
  window.scrollTo(0, 0);
});
</script>
